<template>
  <div ref="root" class="app-select-list inline">
    <v-text-field
      ref="activator"
      class="select-activator"
      :value="selectedLabel"
      :placeholder="placeholder"
      :readonly="true"
      :dense="dense"
      :outlined="outlined"
      :clearable="clearable"
      hide-details
      :append-icon="menu ? 'mdi-chevron-up' : 'mdi-chevron-down'"
      @click.native="toggle"
      @keydown.native="onKeydown"
      @click:clear="clearValue"
    />

    <v-expand-transition>
      <div
        v-show="menu"
        class="list-card"
        role="listbox"
        :aria-expanded="menu ? 'true' : 'false'"
        :style="dropdownStyle"
      >
        <div class="list-wrap" :style="{ maxHeight: listMaxHeightPx || maxListPx, overflowY: 'auto' }">
          <div
            v-for="(item, idx) in normalizedItems"
            :key="idx"
            :class="['option-row', { 'is-active': isSelected(item.value) }]"
            role="option"
            :aria-selected="isSelected(item.value) ? 'true' : 'false'"
            tabindex="0"
            @click="select(item.value)"
            @keydown.enter.prevent="select(item.value)"
            @keydown.space.prevent="select(item.value)"
          >
            <template v-if="variant === 'channel'">
              <div class="row-justify">
                <div class="row-left">
                  <img
                    v-if="item.thumb"
                    :src="item.thumb"
                    alt=""
                    width="32"
                    height="32"
                    class="thumb-32"
                  />
                  <div class="opt-title">{{ item.label }}</div>
                </div>

                <div v-if="item.needReconnect" class="reconnect-badge">
                  <span class="reconnect-dot"></span>
                  <span class="reconnect-text">재연결 필요</span>
                </div>
              </div>
            </template>

            <template v-else-if="variant === 'privacy'">
              <div class="opt-title">{{ item.label }}</div>
              <div class="opt-desc" v-if="item.desc">{{ item.desc }}</div>
            </template>

            <template v-else>
              <div class="opt-title">{{ item.label }}</div>
            </template>
          </div>
        </div>

        <template v-if="variant === 'channel'">
          <div style="height:1px;background:#e5e7eb;margin:0;"></div>
          <div class="channel-actions">
            <v-btn
              type="button"
              class="action-btn"
              block
              outlined
              color="#374151"
              @click="onAddChannel"
            >
              <v-icon left small>mdi-plus</v-icon>
              {{ addChannelLabel }}
            </v-btn>
          </div>
        </template>
      </div>
    </v-expand-transition>
  </div>
</template>

<script>
export default {
  name: 'AppSelectList',
  props: {
    value: { type: [String, Number, Boolean, Object, null], default: null },
    items: { type: Array, required: true },
    itemLabel: { type: String, default: 'label' },
    itemValue: { type: String, default: 'value' },
    itemDesc:  { type: String, default: 'desc' },
    itemThumb: { type: String, default: 'thumb' },
    itemNeedReconnect: { type: String, default: 'needReconnect' },

    placeholder: { type: String, default: '' },
    dense: { type: Boolean, default: true },
    outlined: { type: Boolean, default: true },
    clearable: { type: Boolean, default: false },

    itemHeight: { type: Number, default: 40 },
    showItemsCount: { type: Number, default: 4.5 },

    variant: { type: String, default: 'category' }, // 'channel' | 'privacy' | 'category'
    offset: { type: Number, default: 4 },

  },
  data() {
    return {
      menu: false,
      dropdownStyle: {},
      listMaxHeightPx: null,
    };
  },
  computed: {
    normalizedItems() {
      return this.items.map(it => {
        if (typeof it === 'object') {
          return {
            label: it[this.itemLabel],
            value: it[this.itemValue],
            desc: it[this.itemDesc],
            thumb: it[this.itemThumb],
            needReconnect: Boolean(it[this.itemNeedReconnect]),
          };
        }
        return { label: String(it), value: it, desc: null, thumb: null, needReconnect: false };
      });
    },
    selectedLabel() {
      const found = this.normalizedItems.find(i => this.isSelected(i.value));
      return found ? found.label : '';
    },
    maxListPx() {
      return `${this.itemHeight * this.showItemsCount}px`;
    },
    addChannelLabel() {
      const translator = this.$store && this.$store.getters ? this.$store.getters.t : null;
      return typeof translator === 'function'
        ? translator('social.integration.add_channel')
        : '다른 채널 추가';
    },
  },
  watch: {
    menu(val) {
      if (val) {
        this.$nextTick(() => {
          this.reposition();
          window.addEventListener('resize', this.reposition, { passive: true });
          window.addEventListener('scroll', this.reposition, { passive: true });
        });
      } else {
        window.removeEventListener('resize', this.reposition);
        window.removeEventListener('scroll', this.reposition);
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.onClickOutside, { passive: true });
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onClickOutside);
    window.removeEventListener('resize', this.reposition);
    window.removeEventListener('scroll', this.reposition);
  },
  methods: {
    toggle() { this.menu = !this.menu; },
    isSelected(v) { return this.value === v; },
    select(v) {
      this.$emit('input', v);
      this.$emit('change', v);
      this.menu = false;
    },
    clearValue() {
      this.$emit('input', null);
      this.$emit('change', null);
    },
    onClickOutside(e) {
      const root = this.$refs.root;
      if (root && !root.contains(e.target)) this.menu = false;
    },
    onKeydown(e) {
      if (e.key === 'Escape') this.menu = false;
      else if (e.key === 'Enter') this.toggle();
    },
    reposition() {
      const act = this.$refs.activator && this.$refs.activator.$el ? this.$refs.activator.$el : null;
      if (!act) return;
      const r = act.getBoundingClientRect();
      this.dropdownStyle = {
        position: 'fixed',
        left: `${r.left}px`,
        top: `${r.bottom + this.offset}px`,
        width: `${r.width}px`,
        zIndex: 6000,
      };

      // Dynamically adjust the list max height to fit to the viewport bottom
      const viewportH = window.innerHeight || document.documentElement.clientHeight || 0;
      const margin = 8; // keep small gap from bottom
      const available = Math.max(0, viewportH - (r.bottom + this.offset) - margin);
      const desired = this.itemHeight * this.showItemsCount;
      const minDesired = this.itemHeight * Math.min(2, this.showItemsCount);
      const target = available > 0 ? Math.max(Math.min(available, desired), minDesired) : desired;
      this.listMaxHeightPx = `${Math.round(target)}px`;
    },
    onAddChannel() {
      this.$emit('channel-add');
      this.menu = false;
    },
  },
};
</script>

<style scoped>
.app-select-list.inline { position: relative; cursor: pointer; }

.list-card {
  overflow: hidden;
  border-radius: 4px;
  background: #fff;
  box-shadow: 0 8px 25px rgba(0,0,0,0.12);
}

.list-wrap {
  display: flex;
  padding: 6px;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  align-self: stretch;
  background: #fff;
}

::v-deep(.select-activator .v-input__slot) {
  cursor: pointer;
  user-select: none;
}
::v-deep(.select-activator .v-input__append-inner) {
  cursor: pointer;
}

.option-row {
  display: flex;
  padding: 6px 8px;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  gap: 6px;
  align-self: stretch;
  border-radius: 4px;
  cursor: pointer;
  transition: background-color .12s ease;
}
.option-row:hover { background: #f7f7f7; }
.option-row.is-active { background: #F2F2F2; }

.opt-title { font-weight: 700; line-height: 1.2; }
.opt-desc  { font-size: 12px; color: #666; line-height: 1.35; }

/* 하단 액션 버튼 */
.action-btn {
  appearance: none;
  background: transparent;
  border: 0;
  padding: 8px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: background-color .15s ease;
}
.action-btn:hover { background: #f7f7f7; }

.channel-actions {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 6px 10px;
}

.action-btn {
  border-radius: 8px !important;
  justify-content: flex-start !important;
  text-transform: none !important;
  transition: background-color .15s ease;
}

.action-btn:hover {
  background-color: #f7f7f7 !important; /* gray-100 */
}

.row-justify {
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-self: stretch;
  width: 100%;
}

.row-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.thumb-32 {
  width: 32px;
  height: 32px;
  object-fit: cover;
  border-radius: 6px;
}

/* 재연결 배지 */
.reconnect-badge {
  display: flex;
  align-items: center;
  gap: 4px;
}

.reconnect-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: hsla(39, 100%, 55%, 0.9);
}

.reconnect-text {
  font-size: 12px;
  color: #777;
}
</style>
